<template>
  <section v-if="canCancelSubscription" class="subscription-cancellation-card">
    <header class="subscription-cancellation-card__header">
      <div>
        <h3>{{ tt("billing.manageSubscription", "Gestiona tu suscripción") }}</h3>
        <p>{{ tt("billing.cancelSubscriptionPrompt", "Tu opinión nos ayuda a mejorar Clipr.") }}</p>
      </div>
      <span class="subscription-cancellation-card__plan">
        {{ subscriptionStatus.display_name }}
      </span>
    </header>

    <button
      type="button"
      class="subscription-cancellation-card__button"
      :disabled="loading"
      @click="openCancellationModal"
    >
      <i class="ti-close" aria-hidden="true"></i>
      {{ tt("billing.cancelSubscription", "Cancelar suscripción") }}
    </button>

    <body-portal v-if="showCancellationModal" to="modals">
      <div
        class="confirm-modal-overlay"
        role="dialog"
        aria-modal="true"
        aria-labelledby="subscription-cancel-title"
        @click.self="closeCancellationModal"
      >
        <form class="confirm-modal subscription-cancellation-modal" @submit.prevent="cancelSubscription">
          <div class="confirm-modal-header">
            <h3 id="subscription-cancel-title" class="confirm-modal-title mb-0">
              {{ tt("billing.cancelSubscriptionTitle", "Cancelar suscripción") }}
            </h3>
            <button type="button" class="confirm-modal-close" @click="closeCancellationModal">
              <i class="ti-close"></i>
            </button>
          </div>
          <div class="confirm-modal-body">
            <label class="subscription-cancellation-modal__field">
              <span>{{ tt("billing.cancelReasonLabel", "Motivo de cancelación") }}</span>
              <select v-model="cancellationReasonType" required>
                <option value="" disabled>{{ tt("billing.cancelReasonPlaceholder", "Selecciona un motivo") }}</option>
                <option value="price">{{ tt("billing.cancelReasonPrice", "El precio es muy alto") }}</option>
                <option value="usage">{{ tt("billing.cancelReasonUsage", "No uso Clipr lo suficiente") }}</option>
                <option value="features">{{ tt("billing.cancelReasonFeatures", "No encontré las funciones que necesito") }}</option>
                <option value="technical">{{ tt("billing.cancelReasonTechnical", "Tuve problemas técnicos") }}</option>
                <option value="other">{{ tt("billing.cancelReasonOther", "Otro motivo") }}</option>
              </select>
            </label>
            <label class="subscription-cancellation-modal__field">
              <span>{{ tt("billing.cancelMessageLabel", "Cuéntanos más") }}</span>
              <textarea
                v-model.trim="cancellationMessage"
                rows="4"
                required
                :placeholder="tt('billing.cancelMessagePlaceholder', 'Escribe aquí tu motivo de cancelación')"
              ></textarea>
            </label>
            <p v-if="errorMessage" class="subscription-cancellation-modal__error">{{ errorMessage }}</p>
          </div>
          <div class="confirm-modal-footer">
            <button type="button" class="confirm-modal-btn confirm-modal-btn--secondary" @click="closeCancellationModal">
              {{ tt("common.cancel", "Cancelar") }}
            </button>
            <button type="submit" class="confirm-modal-btn confirm-modal-btn--danger" :disabled="loading">
              <span v-if="loading"><i class="ti-reload ti-spin"></i> {{ tt("billing.cancellingSubscription", "Cancelando...") }}</span>
              <span v-else>{{ tt("billing.confirmCancellation", "Confirmar cancelación") }}</span>
            </button>
          </div>
        </form>
      </div>
    </body-portal>
  </section>
</template>

<script>
import { supabase } from "@/supabase";
import { getApiUrl } from "@/api/client";
import { createCancellationFeedback } from "@/api/suggestions";
import { buildAuthHeaders } from "@/utils/impersonation";
import { useCurrentUserStore } from "@/stores/currentUser";

export default {
  name: "SubscriptionCancellationCard",
  data() {
    return {
      subscriptionStatus: null,
      showCancellationModal: false,
      cancellationReasonType: "",
      cancellationMessage: "",
      errorMessage: "",
      loading: false,
    };
  },
  computed: {
    currentSubscriptionStatus() {
      const status = String(this.subscriptionStatus?.local_status || this.subscriptionStatus?.provider_status || "").toLowerCase();
      return status === "cancelled" ? "canceled" : status;
    },
    canCancelSubscription() {
      return !!this.subscriptionStatus?.subscription_id
        && !this.subscriptionStatus?.cancel_at_period_end
        && ["active", "pending"].includes(this.currentSubscriptionStatus);
    },
  },
  async mounted() {
    await this.refreshSubscriptionStatus();
  },
  methods: {
    tt(key, fallback) {
      return typeof this.$t === "function" ? this.$t(key) : fallback;
    },
    async getSessionOrFail() {
      const { data: { session }, error } = await supabase.auth.getSession();
      if (error || !session?.access_token) {
        throw new Error(this.tt("common.loginFirst", "Inicia sesión primero."));
      }
      return session;
    },
    async refreshSubscriptionStatus() {
      const session = await this.getSessionOrFail();
      const payload = await useCurrentUserStore().syncCommerceStatus(session, { force: true });
      this.subscriptionStatus = payload.subscriptionStatus;
    },
    openCancellationModal() {
      this.cancellationReasonType = "";
      this.cancellationMessage = "";
      this.errorMessage = "";
      this.showCancellationModal = true;
    },
    closeCancellationModal() {
      if (this.loading) return;
      this.showCancellationModal = false;
      this.errorMessage = "";
    },
    async cancelSubscription() {
      if (!this.cancellationReasonType || !this.cancellationMessage) {
        this.errorMessage = this.tt(
          "billing.cancelReasonRequired",
          "Selecciona un motivo y escribe un mensaje para continuar."
        );
        return;
      }

      this.loading = true;
      this.errorMessage = "";
      try {
        const session = await this.getSessionOrFail();
        const cancellationPayload = {
          subscription_id: String(this.subscriptionStatus.subscription_id),
          reason: `${this.cancellationReasonType}: ${this.cancellationMessage}`,
        };
        if (this.subscriptionStatus.provider_subscription_id) {
          cancellationPayload.provider_subscription_id = String(this.subscriptionStatus.provider_subscription_id);
        }
        await createCancellationFeedback(session, {
          reasonType: this.cancellationReasonType,
          message: this.cancellationMessage,
        });
        const response = await fetch(`${getApiUrl()}/momo/ecartpay/subscriptions/cancel`, {
          method: "POST",
          headers: buildAuthHeaders(session),
          body: JSON.stringify(cancellationPayload),
        });
        if (!response.ok) {
          throw new Error(await response.text());
        }

        const payload = await response.json();
        this.$notify({
          message: payload.message || this.tt("billing.subscriptionCancelledNotice", "Suscripción cancelada correctamente."),
          type: "success",
        });
        this.showCancellationModal = false;
        await this.refreshSubscriptionStatus();
      } catch (error) {
        this.errorMessage = error.message;
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped lang="scss">
@import "@/assets/sass/clipr/confirm-modal";

.subscription-cancellation-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-top: 16px;
  padding: 16px;
  border: 1px solid rgba(248, 113, 113, 0.28);
  border-radius: 14px;
  background: rgba(248, 113, 113, 0.06);
}

.subscription-cancellation-card__header {
  display: flex;
  align-items: center;
  gap: 10px;
}

.subscription-cancellation-card__header h3 {
  margin: 0 0 4px;
  color: var(--clipr-text-primary);
  font-size: 0.9rem;
  font-weight: 700;
}

.subscription-cancellation-card__header p {
  margin: 0;
  color: var(--clipr-text-secondary);
  font-size: 0.78rem;
}

.subscription-cancellation-card__plan {
  padding: 4px 8px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.07);
  color: var(--clipr-text-secondary);
  font-size: 0.7rem;
  font-weight: 700;
  white-space: nowrap;
}

.subscription-cancellation-card__button {
  flex-shrink: 0;
  padding: 8px 12px;
  border: 1px solid rgba(248, 113, 113, 0.52);
  border-radius: 8px;
  background: rgba(239, 68, 68, 0.14);
  color: #fecaca;
  font-size: 0.72rem;
  font-weight: 700;
}

.subscription-cancellation-card__button:hover:not(:disabled) {
  background: rgba(239, 68, 68, 0.24);
}

.subscription-cancellation-modal {
  width: min(100%, 460px);
}

.subscription-cancellation-modal__field {
  display: block;
  margin-bottom: 16px;
}

.subscription-cancellation-modal__field > span {
  display: block;
  margin-bottom: 7px;
  color: var(--clipr-text-primary);
  font-size: 0.8rem;
  font-weight: 700;
}

.subscription-cancellation-modal select,
.subscription-cancellation-modal textarea {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid var(--clipr-border-color);
  border-radius: 9px;
  background: var(--clipr-bg-elevated);
  color: var(--clipr-text-primary);
  font: inherit;
}

.subscription-cancellation-modal textarea {
  resize: vertical;
}

.subscription-cancellation-modal__error {
  margin: -4px 0 0;
  color: #fca5a5;
  font-size: 0.8rem;
}

.confirm-modal-btn--danger {
  border-color: rgba(248, 113, 113, 0.5);
  background: rgba(239, 68, 68, 0.17);
  color: #fee2e2;
}

.ti-spin {
  display: inline-block;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

@media (max-width: 767px) {
  .subscription-cancellation-card {
    align-items: flex-start;
    flex-direction: column;
  }
}
</style>
